import type { StudioProject } from '../domain/studioTypes'
import { STUDIO_MODES } from '../data/studioCatalog'
import { VOXNELA_50_STYLES } from '../../data/voxnela50Styles'
import { getTaskById } from '../../data/voxnela56Tasks'
import { localizeTask } from '../../i18n/catalog'
import { supportedLocales, type SupportedLocale } from '../../types/simulation'

const freePromptTitles: Record<SupportedLocale, string> = {
  tr: 'Görsel üretimi', en: 'Image creation', de: 'Bilderstellung', es: 'Creación de imágenes',
  fr: 'Création d’image', ru: 'Создание изображения', it: 'Creazione immagini', pt: 'Criação de imagem',
  sv: 'Bildskapande', nl: 'Afbeelding maken', ar: 'إنشاء صورة', hi: 'चित्र बनाना',
  id: 'Pembuatan gambar', ja: '画像の作成', ko: '이미지 만들기', pl: 'Tworzenie obrazu', th: 'สร้างภาพ',
}

/**
 * Returns a clean, concise style name for the header.
 */
export function getProjectStyleTitle(project: StudioProject, fallback = 'Voxnela Style', locale = 'en'): string {
  if ((project.versions[0]?.localEdit || project.versions[0]?.source === 'upload') && project.title.trim()) return project.title
  const appLocale: SupportedLocale = supportedLocales.includes(locale as SupportedLocale)
    ? locale as SupportedLocale
    : 'en'
  // Free-prompt projects have no selected style; keep their header short and localized.
  if (!project.subcategory || project.subcategory === 'default') return freePromptTitles[appLocale]

  if (project.subcategory) {
    const task = getTaskById(project.subcategory)
    if (task) return localizeTask(task, appLocale).title
  }

  // 1. Direct subcategory match in VOXNELA_50_STYLES
  if (project.subcategory) {
    const vox = VOXNELA_50_STYLES.find(
      (s) =>
        s.id === project.subcategory ||
        s.id.replace(/_/g, '-') === project.subcategory.replace(/_/g, '-') ||
        s.title.toLowerCase() === project.subcategory.toLowerCase(),
    )
    if (vox) return vox.title

    for (const mode of STUDIO_MODES) {
      const sub = mode.subcategories.find(
        (s) => s.id === project.subcategory || s.id.replace(/_/g, '-') === project.subcategory.replace(/_/g, '-'),
      )
      if (sub) return locale === 'tr'
        ? sub.label
        : sub.id.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase())
    }
  }

  // 2. Prompt match against VOXNELA_50_STYLES
  const promptText = (project.prompt || project.title || '').toLowerCase()
  const matchedVox = VOXNELA_50_STYLES.find(
    (s) =>
      (s.realPrompt && promptText.includes(s.realPrompt.slice(0, 30).toLowerCase())) ||
      (s.title && promptText.includes(s.title.toLowerCase())),
  )
  if (matchedVox) return matchedVox.title

  // 3. Subcategory match in STUDIO_MODES
  for (const mode of STUDIO_MODES) {
    for (const sub of mode.subcategories) {
      if (
        (sub.samplePrompt && promptText.includes(sub.samplePrompt.slice(0, 25).toLowerCase())) ||
        (sub.label && promptText.includes(sub.label.toLowerCase()))
      ) {
        return locale === 'tr'
          ? sub.label
          : sub.id.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase())
      }
    }
  }

  // 4. If title is a prompt fragment, extract primary clause
  if (project.title && project.title.trim()) {
    const parts = project.title.split(/[,;\n–—]/)
    const primary = parts[0]?.trim()
    if (primary && primary.length >= 3 && primary.length <= 32) {
      return primary
    }
    if (project.title.length > 32) {
      return project.title.slice(0, 30).trim() + '…'
    }
    return project.title
  }

  return fallback
}

/**
 * Formats production date (only date, e.g. "18 Eyl 2026").
 */
export function formatProductionDate(timestampMs?: number, locale = 'tr-TR'): string {
  const ts = timestampMs && timestampMs > 0 ? timestampMs : Date.now()
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(ts)
  } catch {
    const d = new Date(ts)
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`
  }
}

export interface StudioImageDetailItem {
  id: string
  imageUrl: string
  title?: string
  subtitle?: string
  project?: StudioProject
  version?: number
  projectId?: string
  aspectRatio?: string
  createdAtMs?: number
}
